<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, nextTick, type Component } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { onClickOutside, useScroll } from '@vueuse/core'
import { VButton, VNotification } from '@/modules/shared/components'
import { useWorkspaceOnboardingStore } from '../stores/workspace-onboarding-store'
import { useWorkspaceSettingsStore } from '../stores/workspace-settings-store'
import { useCandidateStore } from '@/modules/connecting/stores/candidate-store'
import { useUnivesrityStore } from '@/modules/shared/stores/university-store'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'

import OnboardingProgressBar from '@/modules/connecting/components/onboarding-progress-bar.vue'
import VShimmerLogo from '@/modules/connecting/components/v-shimmer-logo.vue'

import {
  Building2,
  TrendingUp,
  Grid2x2,
  FileText,
  GraduationCap,
  Globe,
  Clock,
  ShieldCheck,
  Users,
  Settings,
  CheckCircle,
} from 'lucide-vue-next'

import StepFirmType from '../components/onboarding/step-firm-type.vue'
import StepStages from '../components/onboarding/step-stages.vue'
import StepIndustries from '../components/onboarding/step-industries.vue'
import StepThesis from '../components/onboarding/step-thesis.vue'
import StepUniversity from '../components/onboarding/step-university.vue'
import StepWorkspace from '../components/onboarding/step-workspace.vue'
import StepTimezoneCurrency from '../components/onboarding/step-timezone-currency.vue'
import StepRia from '../components/onboarding/step-ria.vue'
import StepTeam from '../components/onboarding/step-team.vue'
import StepSetupMode from '../components/onboarding/step-setup-mode.vue'
import StepReview from '../components/onboarding/step-review.vue'
import StepSuccess from '../components/onboarding/step-success.vue'

const route = useRoute()
const router = useRouter()
const onboardingStore = useWorkspaceOnboardingStore()
const settingsStore = useWorkspaceSettingsStore()
const candidateStore = useCandidateStore()
const universityStore = useUnivesrityStore()
const notificationStore = useNotificationStore()
const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

///////////////////////////////////////////////////////////////////////////////
// Steps
///////////////////////////////////////////////////////////////////////////////

const STEPS: { key: string; icon: Component; title: string; subtitle: string }[] = [
  {
    key: 'firm_type',
    icon: Building2,
    title: 'What kind of firm are you?',
    subtitle: "Select all that apply. We'll tailor your workspace to match how you operate.",
  },
  {
    key: 'stages',
    icon: TrendingUp,
    title: 'What stages do you invest in?',
    subtitle: 'The more specific you are, the stronger your matches will be.',
  },
  {
    key: 'industries',
    icon: Grid2x2,
    title: 'Which industries are you focused on?',
    subtitle: "We'll use this to surface the most relevant investor matches for you.",
  },
  {
    key: 'thesis',
    icon: FileText,
    title: "What's your investment thesis?",
    subtitle: 'Investors you invite will see this first. Be specific about your edge.',
  },
  {
    key: 'university',
    icon: GraduationCap,
    title: 'Any required university affiliations?',
    subtitle: 'Select universities if your firm requires investors to be part of a specific alumni network.',
  },
  {
    key: 'team',
    icon: Users,
    title: "Who's on your team?",
    subtitle: "Paste a LinkedIn URL and we'll pull their photo, name, and bio. You can always add more later.",
  },
  {
    key: 'workspace',
    icon: Globe,
    title: 'Name your workspace',
    subtitle: "Your firm's home on Venture360 — where investors log in and companies apply.",
  },
  {
    key: 'timezone_currency',
    icon: Clock,
    title: 'Time zone and currency',
    subtitle: 'These defaults apply across reports, notifications, and your investor portal.',
  },
  {
    key: 'ria',
    icon: ShieldCheck,
    title: 'Are you a Registered Investment Advisor?',
    subtitle: "We'll set up the right compliance settings and disclosures for your workspace.",
  },
  {
    key: 'setup_mode',
    icon: Settings,
    title: 'Want to start in setup mode?',
    subtitle: "Add investors and companies at your own pace — no one gets notified until you're ready.",
  },
  {
    key: 'review',
    icon: CheckCircle,
    title: 'Does everything look right?',
    subtitle: 'Click any row to make changes, or finish to launch your workspace.',
  },
]

const STEP_KEYS = STEPS.map((s) => s.key)
const TOTAL_STEPS = STEPS.length

// Step refs
const stepRefs = ref<Array<{ submit: () => Promise<boolean> } | null>>([])

const setStepRef = (index: number) => (el: any) => {
  stepRefs.value[index] = el
}

const isEditMode = computed(() => route.name === 'connecting.groups.edit')

const currentStep = ref(0)
const editingFromReview = ref(false)
const showSuccess = ref(false)
const submitting = ref(false)

const currentMeta = computed(() => STEPS[currentStep.value])
const isFirstStep = computed(() => currentStep.value === 0)
const isReviewStep = computed(() => currentStep.value === TOTAL_STEPS - 1)
const isStepBusy = computed(() => !!stepRefs.value[currentStep.value]?.isChecking)

// Interactive stepper
const highestStepReached = ref(0)
const stepMenuOpen = ref(false)
const stepNavRef = ref<HTMLElement | null>(null)

onClickOutside(stepNavRef, () => {
  stepMenuOpen.value = false
})

const isStepReachable = (idx: number) => idx <= highestStepReached.value

const onStepClick = (idx: number) => {
  if (isStepReachable(idx)) {
    currentStep.value = idx
    stepMenuOpen.value = false
  }
}

///////////////////////////////////////////////////////////////////////////////
// Scroll-to-bottom indicator
///////////////////////////////////////////////////////////////////////////////

const scrollContainer = ref<HTMLElement | null>(null)
const { arrivedState } = useScroll(scrollContainer)
const canScrollDown = computed(() => !arrivedState.bottom)

const scrollToBottom = () => {
  scrollContainer.value?.scrollTo({ top: scrollContainer.value.scrollHeight, behavior: 'smooth' })
}

watch(currentStep, () => nextTick(() => scrollContainer.value?.dispatchEvent(new Event('scroll'))))

///////////////////////////////////////////////////////////////////////////////
// Data Loading
///////////////////////////////////////////////////////////////////////////////

onMounted(async () => {
  if (route.query.success) {
    showSuccess.value = true
    return
  }

  showSkeleton()
  await Promise.all([onboardingStore.fetch(), universityStore.fetchUSUniversities()])

  if (onboardingStore.onboarding?.is_complete && !isEditMode.value) {
    router.push({ name: 'dashboard' })
    return
  }

  if (onboardingStore.workspace?.connecting_candidate_id) {
    await candidateStore.fetchCandidate(onboardingStore.workspace.connecting_candidate_id)
  }

  if (isEditMode.value) {
    // Edit mode: start at review step with all steps reachable
    const reviewIndex = TOTAL_STEPS - 1
    currentStep.value = reviewIndex
    highestStepReached.value = reviewIndex
  } else {
    // Restore position from backend
    const savedStep = onboardingStore.onboarding?.current_step
    if (savedStep) {
      const savedIndex = STEP_KEYS.indexOf(savedStep)
      if (savedIndex >= 0) {
        currentStep.value = savedIndex
        highestStepReached.value = savedIndex
      }
    }
  }

  hideSkeleton()
})

///////////////////////////////////////////////////////////////////////////////
// Navigation
///////////////////////////////////////////////////////////////////////////////

const goToStep = (step: number) => {
  currentStep.value = step
  if (step > highestStepReached.value) highestStepReached.value = step
  scrollContainer.value?.scrollTo({ top: 0 })
}

const nextStep = () => {
  if (editingFromReview.value) {
    editingFromReview.value = false
    goToStep(TOTAL_STEPS - 1)
  } else {
    goToStep(currentStep.value + 1)
  }
}

///////////////////////////////////////////////////////////////////////////////
// Optimistic persistence (UI advances immediately, saves run in background)
///////////////////////////////////////////////////////////////////////////////

let saveQueue: Promise<void> = Promise.resolve()

const queueSave = (stepIndex: number, stepKey: string) => {
  const step = stepRefs.value[stepIndex]
  saveQueue = saveQueue
    .then(async () => {
      if (step) await step.submit()
      await onboardingStore.advanceStep(stepKey)
    })
    .catch(() => notificationStore.enqueue('error', "Your changes couldn't be saved. Please go back and try again."))
}

const onClickContinue = async () => {
  if (showSuccess.value) return

  const stepIndex = currentStep.value
  const stepKey = STEP_KEYS[stepIndex]

  // Workspace step: validate + save synchronously (subdomain may trigger redirect)
  if (stepKey === 'workspace') {
    submitting.value = true
    try {
      const step = stepRefs.value[stepIndex]
      if (!step) return
      const success = await step.submit()
      if (!success) return
      onboardingStore.advanceStep(stepKey).catch(() => {})
      nextStep()
    } finally {
      submitting.value = false
    }
    return
  }

  // Review step: wait for in-flight saves, then finish
  if (isReviewStep.value) {
    submitting.value = true
    try {
      await saveQueue
      if (isEditMode.value) {
        // Enable connections matching now that the profile is complete
        await settingsStore.updateSettings({ connections_matching_enabled: true }, { silent: true })
        router.push({ name: 'connecting.groups' })
      } else {
        await onboardingStore.advanceStep(stepKey)
        showSuccess.value = true
      }
    } finally {
      submitting.value = false
    }
    return
  }

  // All other steps: advance UI immediately, save in background
  nextStep()
  queueSave(stepIndex, stepKey)
}

const onClickBack = () => {
  if (showSuccess.value || isFirstStep.value) return
  goToStep(currentStep.value - 1)
}

const onNavigateFromReview = (stepIndex: number) => {
  editingFromReview.value = true
  goToStep(stepIndex)
}

///////////////////////////////////////////////////////////////////////////////
// Arrow key navigation (forward auto-saves, back is browse-only)
///////////////////////////////////////////////////////////////////////////////

const REVIEW_STEP = TOTAL_STEPS - 1

const onKeydown = async (e: KeyboardEvent) => {
  if (showSuccess.value || skeleton.value) return
  if (
    e.target instanceof HTMLInputElement ||
    e.target instanceof HTMLTextAreaElement ||
    e.target instanceof HTMLSelectElement
  )
    return

  if (e.key === 'ArrowLeft' && currentStep.value > 0) {
    e.preventDefault()
    goToStep(currentStep.value - 1)
  } else if (e.key === 'ArrowRight' && currentStep.value < REVIEW_STEP) {
    e.preventDefault()
    const stepIndex = currentStep.value
    const stepKey = STEP_KEYS[stepIndex]
    goToStep(currentStep.value + 1)
    queueSave(stepIndex, stepKey)
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <div class="relative flex max-h-screen min-h-screen flex-col overflow-hidden bg-gray-50">
    <VNotification />

    <!-- Top: progress + logo (fixed, transparent) -->
    <div class="ob-topbar pointer-events-none fixed inset-x-0 top-0 z-50">
      <OnboardingProgressBar :current="showSuccess ? TOTAL_STEPS - 1 : currentStep" :total="TOTAL_STEPS" />
      <div class="ob-header flex items-center justify-between px-6 pt-5">
        <img src="/venture360-logo.svg" class="h-6 w-auto" alt="Venture360" />
        <div v-if="!showSuccess" class="relative" ref="stepNavRef">
          <button
            class="flex cursor-pointer items-center rounded-full border-none bg-white py-[5px] pl-[14px] pr-[10px] font-[inherit] text-[13px] leading-none shadow-sm ring-1 ring-gray-700/20 transition-all duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] [font-variant-numeric:tabular-nums] hover:shadow-md hover:ring-gray-700/30 active:translate-y-px active:shadow-sm"
            @click="stepMenuOpen = !stepMenuOpen"
          >
            <span class="font-bold text-[#009192]">{{ currentStep + 1 }}</span>
            <span class="mx-1 font-normal text-gray-300">/</span>
            <span class="font-medium text-gray-400">{{ TOTAL_STEPS }}</span>
            <svg
              class="ml-1 text-gray-400 transition-transform duration-150 ease-linear"
              :class="{ 'rotate-180': stepMenuOpen }"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="12"
              height="12"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <div
            v-if="stepMenuOpen"
            class="step-menu z-60 absolute right-0 top-[calc(100%+8px)] min-w-[260px] origin-top rounded-xl bg-white p-1.5 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.06)]"
          >
            <button
              v-for="(step, idx) in STEPS"
              :key="step.key"
              class="flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-none bg-transparent px-3 py-2 text-left font-[inherit] text-[13px] font-medium text-gray-700 transition-colors duration-100 enabled:hover:bg-gray-100 enabled:active:bg-gray-200"
              :class="{
                'bg-[rgba(0,145,146,0.06)]': idx === currentStep,
                'cursor-not-allowed opacity-40': !isStepReachable(idx),
              }"
              :disabled="!isStepReachable(idx)"
              @click="onStepClick(idx)"
            >
              <span
                class="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
                :class="idx === currentStep ? 'bg-[#009192] text-white' : 'bg-gray-100 text-gray-500'"
                >{{ idx + 1 }}</span
              >
              <span
                :class="
                  idx === currentStep ? 'font-semibold text-[#009192]' : !isStepReachable(idx) ? 'text-gray-400' : ''
                "
                >{{ step.title }}</span
              >
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Scrollable content -->
    <div ref="scrollContainer" class="ob-scroll flex flex-1 justify-center overflow-y-auto [scrollbar-width:none]">
      <div
        v-if="!showSuccess"
        class="ob-content flex w-full max-w-[600px] flex-col px-5 pt-[72px] max-sm:px-4 max-sm:pt-8"
      >
        <!-- Loading -->
        <div v-if="skeleton" class="flex flex-1 items-center justify-center py-20">
          <VShimmerLogo />
        </div>

        <div v-else class="flex flex-1 flex-col">
          <!-- Step content (keyed for entrance animation) -->
          <div :key="currentStep" class="step-body flex flex-1 flex-col">
            <div
              class="step-icon mb-6 flex h-14 w-14 items-center justify-center rounded-2xl border border-[rgba(0,145,146,0.12)] bg-[linear-gradient(135deg,rgba(0,145,146,0.08),rgba(0,132,180,0.08))] text-[#009192]"
            >
              <component :is="currentMeta.icon" :size="24" :stroke-width="1.5" />
            </div>

            <h2 class="mb-2 text-[26px] font-bold leading-[1.2] tracking-tight text-gray-900 max-sm:text-[22px]">
              {{ currentMeta.title }}
            </h2>
            <p class="mb-9 text-[15px] leading-[1.6] text-gray-500 [text-wrap:pretty]">
              {{ currentMeta.subtitle }}
            </p>

            <div class="flex-1">
              <div v-show="currentStep === 0"><StepFirmType :ref="setStepRef(0)" /></div>
              <div v-show="currentStep === 1"><StepStages :ref="setStepRef(1)" /></div>
              <div v-show="currentStep === 2"><StepIndustries :ref="setStepRef(2)" /></div>
              <div v-show="currentStep === 3"><StepThesis :ref="setStepRef(3)" /></div>
              <div v-show="currentStep === 4"><StepUniversity :ref="setStepRef(4)" /></div>
              <div v-show="currentStep === 5"><StepTeam :ref="setStepRef(5)" /></div>
              <div v-show="currentStep === 6"><StepWorkspace :ref="setStepRef(6)" /></div>
              <div v-show="currentStep === 7"><StepTimezoneCurrency :ref="setStepRef(7)" /></div>
              <div v-show="currentStep === 8"><StepRia :ref="setStepRef(8)" /></div>
              <div v-show="currentStep === 9"><StepSetupMode :ref="setStepRef(9)" /></div>
              <div v-show="currentStep === 10">
                <StepReview :ref="setStepRef(10)" @navigate-to="onNavigateFromReview" />
              </div>
            </div>
          </div>
        </div>
        <div class="h-[90px] shrink-0" />
      </div>

      <!-- Success State -->
      <StepSuccess v-else />
    </div>

    <!-- Scroll-to-bottom indicator -->
    <Transition
      enter-active-class="transition duration-200 ease-[cubic-bezier(0.22,1,0.36,1)]"
      enter-from-class="translate-y-4 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-[cubic-bezier(0.22,1,0.36,1)]"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-4 opacity-0"
    >
      <button
        v-if="canScrollDown && !showSuccess && !skeleton"
        class="scroll-hint fixed bottom-[72px] left-1/2 z-40 flex h-9 w-9 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border-none bg-white/80 shadow-sm ring-1 ring-gray-700/20 backdrop-blur-[16px] transition-shadow duration-150 [backdrop-filter:blur(16px)_saturate(1.5)] hover:shadow-md hover:ring-gray-700/30 active:translate-y-px active:shadow-sm"
        @click="scrollToBottom"
      >
        <svg
          class="scroll-hint-arrow text-gray-400"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M12 5v14m-6-6l6 6 6-6" />
        </svg>
      </button>
    </Transition>

    <!-- Fixed bottom nav bar -->
    <div
      v-if="!showSuccess && !skeleton"
      class="ob-bottombar fixed inset-x-0 bottom-0 z-40 border-t border-[rgba(0,0,0,0.06)] bg-white/80 backdrop-blur-[16px] [backdrop-filter:blur(16px)_saturate(1.5)]"
    >
      <div class="mx-auto flex max-w-[600px] items-center gap-2.5 px-5 py-2.5">
        <VButton v-if="!isFirstStep" variant="secondary" size="lg" :click="onClickBack">← Back</VButton>
        <div v-else />
        <VButton
          v-if="isReviewStep"
          class="flex-1 text-center"
          variant="v-green"
          size="lg"
          :click="onClickContinue"
          :loading="submitting"
          :disabled="isStepBusy"
          >{{ isEditMode ? 'Done' : 'Finish setup' }}</VButton
        >
        <VButton
          v-else
          class="flex-1 text-center"
          variant="v-blue"
          size="lg"
          :click="onClickContinue"
          :loading="submitting"
          :disabled="isStepBusy"
          >Continue →</VButton
        >
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ─── Pointer-events passthrough for topbar children ─── */
.ob-header > * {
  pointer-events: auto;
}

/* ─── Step menu dropdown animation ─── */
.step-menu {
  animation: menuIn 0.15s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes menuIn {
  from {
    opacity: 0;
    transform: perspective(1000px) translateY(-8px) rotateX(-30deg) scale(0.92);
  }
  to {
    opacity: 1;
    transform: perspective(1000px) translateY(0) rotateX(0) scale(1);
  }
}

/* ─── Webkit scrollbar hiding ─── */
.ob-scroll::-webkit-scrollbar {
  display: none;
}

/* ─── Staggered entrance animation ─── */
.step-body > * {
  animation: fadeUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.step-body > :nth-child(1) {
  animation-delay: 0ms;
}
.step-body > :nth-child(2) {
  animation-delay: 50ms;
}
.step-body > :nth-child(3) {
  animation-delay: 75ms;
}
.step-body > :nth-child(4) {
  animation-delay: 120ms;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ─── Scroll-hint arrow bounce (spring: dip → overshoot → settle) ─── */
.scroll-hint-arrow {
  animation: hintBounce 5s cubic-bezier(0.22, 1, 0.36, 1) 3s 3;
}

@keyframes hintBounce {
  0%,
  100% {
    transform: translateY(0);
  }
  6% {
    transform: translateY(4px);
  }
  12% {
    transform: translateY(-1.5px);
  }
  16% {
    transform: translateY(0);
  }
}

/* ─── Reduced motion ─── */
@media (prefers-reduced-motion: reduce) {
  .step-body > *,
  .scroll-hint-arrow {
    animation: none;
  }
}
</style>
